<template>
  <VdPage class="classes-list-page" title="Classes" :subtitle="`(${classes.length})`" :loading="loading">
    <template #actions>
      <VdSwitch
        :model-value="expandAll"
        active-text="Expand all"
        data-cy="expand-all-classes"
        @update:model-value="onExpandAllChanged"
      />
      <InputText
        ref="searchInputRef"
        v-model="searchTerm"
        class="search-input"
        placeholder="Looking for something..."
        data-cy="search-class-input"
        @focusout="forceFocusOnSearchInput"
      />
      <vd-button
        v-if="inWIPVersion && canReadClassesCatalog"
        class="download-catalog-button"
        type="primary"
        icon-left="fa:arrow-down-to-line"
        data-cy="class-catalog-button"
        @click="onOpenClassCatalog"
      >
        {{ canWriteClasses ? "Import from catalog" : "Open classes catalog" }}
      </vd-button>
      <vd-button
        v-if="inWIPVersion && canWriteClasses"
        class="new-class-button"
        type="secondary"
        skin="blue"
        icon-left="fa:link"
        @click="onNewClass"
      >
        Link new class
      </vd-button>
    </template>

    <div class="classes-list">
      <keep-alive v-if="finished && filteredClasses.length === 0">
        <vd-empty :title="searchTerm ? 'not found' : 'no class'">
          <div v-if="searchTerm">
            <p>Your search for a class has no results</p>
          </div>
          <div v-else>
            <p>Sorry, there is no data.</p>
            <p v-if="inWIPVersion">Refresh the page or try the following options:</p>
          </div>
          <template v-if="!searchTerm && inWIPVersion" #actions>
            <vd-button
              v-if="canReadClassesCatalog && canWriteClasses"
              type="primary"
              icon-left="fa:arrow-down-to-line"
              @click="onOpenClassCatalog"
            >
              Import from catalog
            </vd-button>
            <vd-button v-if="canWriteClasses" type="secondary" skin="blue" icon-left="fa:link" @click="onNewClass">
              Link new class
            </vd-button>
          </template>
        </vd-empty>
      </keep-alive>
      <div v-else>
        <ClassesListItem
          v-for="item in filteredClasses"
          :id="item.technicalName"
          :key="JSON.stringify(item)"
          :value="item"
          :game-id="gameId"
          :version-id="versionId"
          :expanded="expandAll || isCurrentHash(item.technicalName)"
          :hide-menu-actions="!inWIPVersion || (!canWriteClasses && !canWriteClassesCatalog)"
          @delete="onDelete"
          @edit="onEdit"
          @export-to-catalog="onExportToCatalog"
        />
      </div>
    </div>

    <!-- warning delete dialog -->
    <vd-dialog
      :visible="displayDeleteWarning"
      hide-close-button
      title="This class can't be deleted"
      type="error"
      @close="closeDeleteWarning"
    >
      <span>You can't delete this class as it is used</span>
      <strong v-if="deleteWarningDetails.configurationRules.length">
        in {{ deleteWarningDetails.configurationRules.length }}
        {{ pluralize("configuration rule", deleteWarningDetails.configurationRules.length) }}
      </strong>
      <span v-if="deleteWarningDetails.abtests.length && deleteWarningDetails.configurationRules.length">
        and&nbsp;
      </span>
      <span v-if="deleteWarningDetails.abtests.length">
        <strong>in the following A/B Test cohorts:</strong>
        <ul class="delete-warning-list">
          <li v-for="abtest in deleteWarningDetails.abtests" :key="abtest.id">
            <em>{{ abtest.cohortName }}</em>
            of the
            <strong>{{ abtest.name }}</strong>
            A/B Test
          </li>
        </ul>
      </span>
      <template #footer>
        <vd-button type="secondary" skin="blue" @click="closeDeleteWarning">I understand</vd-button>
      </template>
    </vd-dialog>

    <!-- This router-view is used for ClassesCatalogImportPage -->
    <router-view />

    <Transition name="scroll-top-fade">
      <button
        v-if="showScrollTop"
        type="button"
        class="scroll-to-top-button"
        aria-label="Scroll to top"
        data-cy="scroll-to-top"
        @click="scrollToTop"
      >
        <VdIcon store="fa" name="arrow-up" size="large" color="white" />
      </button>
    </Transition>
  </VdPage>
</template>

<script setup lang="ts">
import VdIcon from "@/components/VdIcon/VdIcon.vue";
import { VdPage, VdSwitch } from "@voodoo.io/voodoo-ui";
import InputText from "primevue/inputtext";
import { computed, onBeforeUnmount, onMounted, reactive, ref } from "vue";
import { useRouter } from "vue-router";

import { pluralize } from "@/filters";
import {
  ClassesListPageClassInfoFragment,
  DeleteClassMutationResponse,
  queryNames,
  useClassesListPageDeleteMutation,
  useClassesListPageExportMutation,
  useClassesListPageQuery,
  useMutationResponse,
} from "@/graphql";
import useMessages from "@/hooks/messages";
import useRouterHash from "@/hooks/router-hash";
import useSearchFilter from "@/hooks/search-filter";
import usePermissions from "@/hooks/use-permissions";
import links from "@/router/links";

import ClassesListItem from "./components/ClassesListItem.vue";

const props = defineProps({
  gameId: {
    type: String,
    required: true,
  },
  versionId: {
    type: String,
    required: true,
  },

  /**
   * If true, force the classes reloading.
   * This is used when importing a class from the catalog,
   * forcing the reload will update the classes list after import.
   */
  forceReload: {
    type: Boolean,
    default: false,
  },
});
const router = useRouter();
const { canWriteClasses, canReadClassesCatalog, canWriteClassesCatalog } = usePermissions();

const { isCurrentHash, currentHash, setHash } = useRouterHash();
const { confirm } = useMessages();
const expandAll = ref(!currentHash.value);

const onExpandAllChanged = (val: boolean) => {
  expandAll.value = val;
  if (currentHash.value) {
    setHash("");
  }
};

const searchInputRef = ref<{ $el: HTMLInputElement } | null>(null);

/**
 * Keep focus on the search input even when the user clicks elsewhere.
 * `preventScroll: true` is required: without it the browser scrolls the
 * input back into view, which races the click and breaks navigation to
 * any list item that's currently below the fold.
 */
const forceFocusOnSearchInput = () => {
  searchInputRef.value?.$el.focus({ preventScroll: true });
};

/**
 * Fetch the game's classes
 */
const { result, loading: queryClassesLoading } = useClassesListPageQuery(
  {
    gameId: props.gameId,
    versionId: props.versionId,
  },
  { fetchPolicy: "network-only" }
);

const gameBundleId = computed(() => result.value?.game?.bundleId);

const classes = computed<ClassesListPageClassInfoFragment[]>(() => result.value?.game?.version?.classes ?? []);

const finished = computed(() => !queryClassesLoading.value);

const inWIPVersion = computed(() => result.value?.game?.version?.isWip);

/**
 * Filter the features from the searchTerm
 */
const { searchTerm, filteredItems: filteredClasses } = useSearchFilter(classes, [
  "displayName",
  "technicalName",
  "description",
]);

/**
 * Some links
 */

const onNewClass = () => {
  router.push({ name: links.games.classes.new });
};

const onOpenClassCatalog = () => {
  router.push({ name: links.games.classes.importFromCatalog });
};

/**
 *  Delete a class
 */
const { mutate: deleteClass, loading: deleteClassLoading } = useClassesListPageDeleteMutation({
  refetchQueries: [queryNames.ClassesListPage],
});

/**
 * If the class is used in an A/B Test cohort or a configuration rule,
 * we cannot delete it without corrupting the data integrity.
 * We therefore display an information dialog that explain to the user
 * that he has to remove the class from those resource before continuing.
 */
const deleteWarningDetails = reactive<{
  abtests: NonNullable<DeleteClassMutationResponse["usedInAbtests"]>;
  configurationRules: NonNullable<DeleteClassMutationResponse["usedInConfigurationRules"]>;
}>({
  abtests: [],
  configurationRules: [],
});

const displayDeleteWarning = ref(false);

const closeDeleteWarning = () => {
  displayDeleteWarning.value = false;
};

const onDelete = async (classId: string) => {
  const confirmed = await confirm("Do you really want to delete this class?");
  if (confirmed) {
    const response = await deleteClass({ gameId: props.gameId, versionId: props.versionId, classId });
    const deleteResult = useMutationResponse(response, (resp) => resp?.deleteClass);
    if (deleteResult?.isCurrentlyUsed) {
      deleteWarningDetails.abtests = deleteResult?.usedInAbtests ?? [];
      deleteWarningDetails.configurationRules = deleteResult?.usedInConfigurationRules ?? [];
      displayDeleteWarning.value = true;
    }
  }
};

/**
 * Go to edit form
 */
const onEdit = (classId: string) => {
  router.push({
    name: links.games.classes.edit,
    params: {
      gameId: props.gameId,
      versionId: props.versionId,
      classId,
    },
  });
};

/**
 * Export a class to the catalog
 */
const { mutate: exportToCatalog, loading: exportToCatalogLoading } = useClassesListPageExportMutation({
  refetchQueries: [queryNames.ClassesCatalogImportPage],
});

const onExportToCatalog = async (classId: string) => {
  if (!gameBundleId.value) {
    return;
  }
  const response = await exportToCatalog({
    gameId: props.gameId,
    versionId: props.versionId,
    gameBundleId: gameBundleId.value,
    classId,
    override: false,
  });
  const exportResult = useMutationResponse(response, (resp) => resp?.exportClassToCatalog);
  if (!exportResult?.success && exportResult?.isConflict) {
    const confirmed = await confirm("This class already exists in catalog. Do you want to override?", {});
    if (confirmed) {
      const overrideResponse = await exportToCatalog({
        gameId: props.gameId,
        versionId: props.versionId,
        classId,
        gameBundleId: gameBundleId.value,
        override: true,
      });
      useMutationResponse(overrideResponse, (resp) => resp?.exportClassToCatalog);
    }
  }
};

/**
 * The loading status depends on three different loader
 */
const loading = computed(() => queryClassesLoading.value || deleteClassLoading.value || exportToCatalogLoading.value);

/**
 * Scroll to top button: only visible once the list is long enough
 * to have produced meaningful scroll on the page.
 */
const SCROLL_TOP_THRESHOLD = 300;
const showScrollTop = ref(false);

const onWindowScroll = () => {
  showScrollTop.value = window.scrollY > SCROLL_TOP_THRESHOLD;
};

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: "smooth" });
};

onMounted(() => {
  window.addEventListener("scroll", onWindowScroll, { passive: true });
  onWindowScroll();
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", onWindowScroll);
});
</script>
<style lang="scss" scoped>
.classes-list-page {
  .delete-warning-list {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    max-height: 300px;
  }
}

.scroll-to-top-button {
  position: fixed;
  right: 2rem;
  bottom: 2rem;
  z-index: 1000;
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  background-color: var(--vd-color-blue-default);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  transition: background-color 0.2s ease, transform 0.2s ease;

  &:hover {
    background-color: var(--vd-color-blue-dark, var(--vd-color-blue-default));
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
}

.scroll-top-fade-enter-active,
.scroll-top-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.scroll-top-fade-enter-from,
.scroll-top-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>
